import React, {ReactElement} from 'react';
import {useIntl} from 'react-intl';
import {Field, FieldProps} from 'formik';
import RadioIconField, {ButtonInfo} from './RadioIconField';
import {FormikField} from '../types';
import getRequiredMessage from '../validationMessages/getRequiredMessage';

interface Props<FormValues> extends Omit<FormikField, 'name'> {
  name: keyof FormValues;
  options: ButtonInfo[];
  onChange?: (name: string, value: string) => void;
  optionClassname?: string;
}

function RadioIconFieldWithFormikField<FormValues>({
  assistiveMessage,
  label,
  name,
  options,
  placeholder,
  required,
  warningMessage,
  errorMessages,
  validate = () => {
    /* noop */
  },
  onChange = () => {
    /* noop */
  },
  isDisabled,
  useCustom,
  optionClassname,
}: Props<FormValues>): ReactElement {
  const {formatMessage} = useIntl();

  const validateFieldProp = (value: string) => {
    const requiredMessage = errorMessages?.required || '';
    const requiredErrorMessage = getRequiredMessage(
      formatMessage,
      !!useCustom,
      name,
      value,
      !!required,
      requiredMessage
    );
    if (requiredErrorMessage) {
      return requiredErrorMessage;
    }

    // custom validator
    if (value.length > 0) {
      return validate(value);
    }

    return undefined;
  };

  return (
    <Field name={name} validate={validateFieldProp}>
      {({field, meta}: FieldProps) => (
        <RadioIconField
          assistiveMessage={assistiveMessage}
          errorMessage={meta.error}
          label={label}
          name={field.name}
          onChange={(event) => {
            field.onChange(event);
            onChange(field.name, (event.target as HTMLButtonElement).value);
          }}
          onBlur={field.onBlur}
          options={options}
          placeholder={placeholder}
          value={field.value}
          warningMessage={warningMessage}
          isDisabled={isDisabled}
          optionClassname={optionClassname}
        />
      )}
    </Field>
  );
}

export default RadioIconFieldWithFormikField;
